import {
  BOOLEAN_SELECT_LIST_ITEMS,
  Box,
  Button,
  CancelIconButton,
  Card,
  Checkbox,
  CrudMode,
  DataArray,
  DataDisplay,
  formatBoolean,
  formatDate,
  formatPhoneNumber,
  Grid,
  GridBreak,
  IconButton,
  pn,
  Stack,
  Typography,
  UpdateIconButton,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import QrCodeSharpIcon from '@mui/icons-material/QrCodeSharp';
import {
  AddressFields,
  CustomDataModelFieldDisplay,
  CustomDataModelFieldEditor,
  DatePicker,
  Input,
  Select,
  SubmitButton,
  SubmitIconButton,
  DataModelInformation,
} from "components";
import {
  Address,
  LkAcademicNeedId,
  LkEligibilityId,
  LkRaceId,
  LkStudentDisplayFieldId,
  Permission,
  PermissionAction,
  Student,
  StudentCustomIdType,
  StudentEditResource,
  StudentViewResource,
  TenantInfo,
  TextingOptIn as TextingOptInModel,
  DataModelEnum,
} from "generated/openApi";
import produce from "immer";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import SaveIcon from "@mui/icons-material/Save";
import { studentEmailPageRoute } from "../../StudentEmailPage/StudentEmailPage";
import { studentQrPageRoute } from "../../studentqrpage/studentQrPageRoute";
import { PhoneNumberInput } from "components/common/PhoneNumberInput";
import PhonelinkRingIcon from "@mui/icons-material/PhonelinkRing";
import Tooltip from '@mui/material/Tooltip';
import { LockButton } from "components/common/LockButton";
import { textingOptInPageRoute } from "../../StudentTextingOptInPage/TextingOptInPage";

const parseOptionalNumber = (value: string) => (value === "" ? undefined : parseFloat(value));

export const BasicInfo = (props: {
  student: Student;
  tenantTextingInfo: TenantInfo | undefined;
  textingOptIn?: TextingOptInModel | undefined;
  setStudent: (value: Student) => void;
  crudMode: CrudMode;
  fromStudentApplication?: boolean;
  viewResource: StudentViewResource;
  editResource: StudentEditResource;
  validationErrors: ValidationErrors;
  reroute?: (id: number, crudMode: CrudMode) => void;
  save?: () => void;
  hasEditButton?: boolean;
  unsavedChanges?: boolean;
}) => {
  const [isLocked, setIsLocked] = useState(true);
  const [cellPhoneVerified, setCellPhoneVerified] = useState<boolean | null>(null);
  const properties = props.viewResource.properties || [];
  const { breakpointsRef, smallFieldBreakpoints, largeFieldBreakpoints, fullFieldBreakpoints } =
    useElementBreakpoints();
  const navigate = useNavigate();
  const id = props.student.id;

  const displayFields = props.viewResource.displayFields || [];
  const maidenNameDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.MaidenName);
  const feederSchoolDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.FeederSchool);
  const phoneNumberDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.PhoneNumber);
  const workPhoneNumberDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.WorkPhoneNumber);
  const programDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Program);
  const enrollmentDateDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.EnrollmentDate);
  const usCitizenDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.UsCitizen);
  const secondaryEmailDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.SecondaryEmail);
  const atRiskStdTestDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.AtRiskStdTest);
  const atRiskAlgebraDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.AtRiskAlgebra);
  const atRiskLowGpaDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.AtRiskLowGpa);
  const otherNeedDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.OtherNeed);
  const otherSecondaryNeedDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.OtherSecondaryNeed);
  const disconnectedDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Disconnected);
  const deceasedDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Deceased);
  const disabledDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Disabled);
  const pseCohortDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.PseCohort);
  const pseFirstEnrollmentDateDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.PseFirstEnrollmentDate);
  const ninthGradeYearDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.NinthGradeYear);
  const permissionSlipDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.PermissionSlip);
  const collegeBoundDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.CollegeBound);
  const mentorProgramDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.MentorProgram);
  const migrantDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Migrant);
  const sixYearsEslDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.SixYearsEsl);
  const dualEnrollmentDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.DualEnrollment);
  const povertyDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Poverty);
  const firstGenerationDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.FirstGeneration);
  const trioDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Trio);
  const upwardBoundDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.UpwardBound);
  const eopsDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Eops);
  const referralTypeDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.ReferralType);
  const primaryTransportationDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.PrimaryTransportation);
  const reasonForEnrollmentDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.ReasonForEnrollment);
  const graduationYearDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.GraduationYear);
  const graduationTypeDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.GraduationType);
  const cohortDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Cohort);
  const familyIncomeDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.FamilyIncome);
  const noCollegeReasonDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.NoCollegeReason);
  const eligibilityDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Eligibility);
  const academicNeedDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.AcademicNeed);


  const [customId1Focused, setCustomId1Focused] = useState(false);
  const assignedUserDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.AssignedUser);
  const unresponsiveDisplayField = displayFields.find((x) => x.id === LkStudentDisplayFieldId.Unresponsive);

  const showAssignment = assignedUserDisplayField || unresponsiveDisplayField;

  const sortedFields = useMemo(() => {
    return [...displayFields].sort((a, b) => (a.displayOrder || a.id) - (b.displayOrder || b.id));
  }, [displayFields]);

  useEffect(() => {
    if (props.textingOptIn) {
      if (props.textingOptIn.isVerified) {
        setCellPhoneVerified(props.textingOptIn.isVerified);
      }
    }
  }, [props.textingOptIn]);

  const generateCustomId1 = () => {
    ApiService.students.generatedCustomId1().then((response) => {
      props.setStudent(
        produce(props.student, (draft) => {
          draft.customId1 = response;
        })
      );
    });
  };

  const verifyPhoneNumber = () => {
    ApiService.textingConversations.verifyCellPhone(props.student.cellPhone).then((response) => {
      setCellPhoneVerified(response);
    });
  };

  const customField = (id: number, customIdType: StudentCustomIdType) => {
    const propertyName = `customId${id}`;
    const property = properties.find((x) => x.propertyName === propertyName);

    var displayValue = props.student[propertyName];
    if (customIdType.isSsn && isLocked && props.student.id != 0)
      displayValue = "******";

    return props.crudMode === "view" ? (
      <DataDisplay label={customIdType.name}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography>
            {displayValue}
          </Typography>
          {id === 1 && (
            <Tooltip title="View Student QR Code" arrow>
              <IconButton
                color="secondary"
                disabled={!props.student.id}
                size="small"
              >
                <QrCodeSharpIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(studentQrPageRoute.getPath({ studentId: props.student.id }), {
                    });
                  }}
                />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </DataDisplay>
    ) : (
      <Stack spacing="sm">
        <Input
          disabled={props.student.id != 0 && isLocked}
          label={customIdType.name}
          required={customIdType.isRequired}
          errors={props.validationErrors[propertyName]}
          maxLength={property?.maxLength}
          value={(customIdType.isSsn && isLocked && props.student.id !== 0)
            ? "******"
            : (props.student[propertyName] ?? "")
          }
          onChange={(e) =>
            props.setStudent(
              produce(props.student, (draft) => {
                draft[propertyName] = e.target.value;
              })
            )
          }
          onFocus={id === 1 ? () => setCustomId1Focused(true) : undefined}
          onBlur={id === 1 ? () => setCustomId1Focused(false) : undefined}
        />

        {id === 1 && props.editResource.canAutoAssignCustomId && customId1Focused && (
          <Button onMouseDown={() => generateCustomId1()}>Auto assign {customIdType.name}</Button>
        )}
      </Stack>
    );
  };

  const lastNameField = () => {
    const propertyName = pn<Student>("lastName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.student.lastName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.student.lastName}
        onChange={(e) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.lastName = e.target.value;
            })
          )
        }
      />
    );
  };

  const firstNameField = () => {
    const propertyName = pn<Student>("firstName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.student.firstName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.student.firstName}
        onChange={(e) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.firstName = e.target.value;
            })
          )
        }
      />
    );
  };

  const middleNameField = () => {
    const propertyName = pn<Student>("middleName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.student.middleName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.student.middleName}
        onChange={(e) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.middleName = e.target.value;
            })
          )
        }
      />
    );
  };

  const suffixField = () => {
    const propertyName = pn<Student>("suffix");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.student.suffix}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.student.suffix}
        onChange={(e) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.suffix = e.target.value;
            })
          )
        }
      />
    );
  };

  const nickNameField = () => {
    const propertyName = pn<Student>("nickName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.student.nickName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.student.nickName}
        onChange={(e) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.nickName = e.target.value;
            })
          )
        }
      />
    );
  };

  const maidenNameField = () => {
    const propertyName = pn<Student>("maidenName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={maidenNameDisplayField?.displayName}>{props.student.maidenName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        label={maidenNameDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.student.maidenName}
        onChange={(e) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.maidenName = e.target.value;
            })
          )
        }
      />
    );
  };

  const dateOfBirthField = () => {
    const propertyName = pn<Student>("dateOfBirth");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.student.dateOfBirth)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.student.dateOfBirth}
        onChange={(value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.dateOfBirth = value;
            })
          )
        }
      />
    );
  };

  const ageField = () => {
    const dateOfBirth = moment(props.student.dateOfBirth);
    let age: number | undefined;

    if (dateOfBirth.isValid()) {
      age = moment().diff(dateOfBirth, "years");
    } else {
      age = undefined;
    }

    return <DataDisplay label={"Age"}>{age}</DataDisplay>;
  };

  const genderField = () => {
    const propertyName = pn<Student>("genderId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.student.gender?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.genders?.find((x) => x.id === props.student.genderId)}
        options={props.editResource.genders}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.genderId = value?.id;
            })
          )
        }
      />
    );
  };

  const raceField = () => {
    const propertyName = pn<Student>("raceId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.student.race?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.races?.find((x) => x.id === props.student.raceId)}
        options={props.editResource.races}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.raceId = value?.id;
            })
          )
        }
      />
    );
  };

  const racesField = () => {
    const propertyName = pn<Student>("races");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <DataArray value={props.student.races?.map((x) => x.name)} />
      </DataDisplay>
    ) : (
      <Select
        multiple
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.student.races}
        options={props.editResource.races?.filter((x) => x.id !== LkRaceId.TwoOrMoreRaces)}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.races = value;
            })
          )
        }
      />
    );
  };

  const ethnicityField = () => {
    const propertyName = pn<Student>("ethnicityId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.student.ethnicity?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.ethnicities?.find((x) => x.id === props.student.ethnicityId)}
        options={props.editResource.ethnicities}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.ethnicityId = value?.id;
            })
          )
        }
      />
    );
  };

  const programField = () => {
    const propertyName = pn<Student>("programId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={programDisplayField?.displayName}>{props.student.program?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={programDisplayField?.displayName}
        required={programDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.programs?.find((x) => x.id === props.student.programId)}
        options={props.editResource.programs}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.programId = value?.id;
            })
          )
        }
      />
    );
  };

  const emailField = () => {
    const propertyName = pn<Student>("email");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="inherit">{props.student.email}</Typography>

          <IconButton color="secondary" disabled={!props.student.email} size="small">
            <ForwardToInboxIcon
              onClick={(e) => {
                e.stopPropagation();
                navigate(studentEmailPageRoute.getPath({ studentId: props.student.id, screenType: "dialog" }));
              }}
            />
          </IconButton>
        </Stack>
      </DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.student.email}
        onChange={(e) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.email = e.target.value;
            })
          )
        }
      />
    );
  };

  const phoneField = () => {
    const propertyName = pn<Student>("phone");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={phoneNumberDisplayField?.displayName}>{formatPhoneNumber(props.student.phone)}</DataDisplay>
    ) : (
      <PhoneNumberInput
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        label={phoneNumberDisplayField?.displayName}
        value={props.student.phone}
        updatePhoneNumber={(p) => {
          props.setStudent(
            produce(props.student, (draft) => {
              draft.phone = p;
            })
          )
        }}
      />
    );
  };

  const workPhoneField = () => {
    const propertyName = pn<Student>("workPhone");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={workPhoneNumberDisplayField?.displayName}>{formatPhoneNumber(props.student.workPhone)}</DataDisplay>
    ) : (
      <PhoneNumberInput
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        label={workPhoneNumberDisplayField?.displayName}
        value={props.student.workPhone}
        updatePhoneNumber={(p) => {
          props.setStudent(
            produce(props.student, (draft) => {
              draft.workPhone = p;
            })
          )
        }}
      />
    );
  };

  const cellPhoneField = () => {
    const propertyName = pn<Student>("cellPhone");
    const property = properties.find((x) => x.propertyName === propertyName);
    const hasTextingOptInEntry = !!props.textingOptIn?.id && props.textingOptIn.id > 0;
    const textingOptInIconColor = hasTextingOptInEntry
      ? props.textingOptIn?.optedIn
        ? "success.main"
        : "error.main"
      : undefined;

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="inherit">{formatPhoneNumber(props.student.cellPhone)}</Typography>

          {props.student.cellPhone && (<IconButton color="secondary" size="small">
            <PhonelinkRingIcon
              sx={{ color: textingOptInIconColor }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(textingOptInPageRoute.getPath({ personId: props.student.id, isParent: false, screenType: "dialog" }));
              }}
            />
          </IconButton>)}
        </Stack>
      </DataDisplay>
    ) : (
      <PhoneNumberInput
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        label={phoneNumberDisplayField?.displayName}
        value={props.student.cellPhone}
        updatePhoneNumber={(p) => {
          props.setStudent(
            produce(props.student, (draft) => {
              draft.cellPhone = p;
              if (draft.creatingTextingOptIn) {
                draft.creatingTextingOptIn.phoneNumber = p || "";
              }
            })
          )
        }}
      />
    );
  };

  const textingOptInFields = () => {
    if (props.crudMode === "view") return null;

    const textingOptIn = props.student.creatingTextingOptIn;
    const optInMethodPropertyName = pn<TextingOptInModel>("optInMethodId");
    const optInValidationErrors =
      props.validationErrors[pn<Student>("creatingTextingOptIn")] || {};

    return (
      <Stack spacing="sm">
        <Checkbox
          label="Texting Opt-In"
          checked={!!textingOptIn}
          onChange={(e) =>
            props.setStudent(
              produce(props.student, (draft) => {
                draft.creatingTextingOptIn = e.target.checked
                  ? ({
                    phoneNumber: draft.cellPhone || "",
                    optInDate: new Date(),
                  } as TextingOptInModel)
                  : undefined;
              })
            )
          }
        />
        {textingOptIn && (
          <Select
            label="Texting Opt-In Method"
            required
            errors={optInValidationErrors[optInMethodPropertyName]}
            value={props.editResource.optInMethods?.find(
              (x) => x.id === textingOptIn.optInMethodId
            )}
            options={props.editResource.optInMethods}
            getOptionLabel={(option) => option.name}
            onChange={(e, value) =>
              props.setStudent(
                produce(props.student, (draft) => {
                  draft.creatingTextingOptIn!.optInMethodId = value?.id;
                })
              )
            }
          />
        )}
      </Stack>
    );
  };

  const enrollmentDateField = () => {
    const propertyName = pn<Student>("enrollmentDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={enrollmentDateDisplayField?.displayName}>
        {formatDate(props.student.enrollmentDate)}
      </DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        required={enrollmentDateDisplayField?.required}
        label={enrollmentDateDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.student.enrollmentDate}
        onChange={(value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.enrollmentDate = value;
            })
          )
        }
      />
    );
  };

  const feederSchoolField = () => {
    const propertyName = pn<Student>("feederSchoolId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={feederSchoolDisplayField?.displayName}>{props.student.feederSchool?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={feederSchoolDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.feederSchools?.find((x) => x.id === props.student.feederSchoolId)}
        options={props.editResource.feederSchools}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.feederSchoolId = value?.id;
            })
          )
        }
      />
    );
  };

  const GraduationYearField = () => {
    const propertyName = pn<Student>("graduationYear");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={graduationYearDisplayField?.displayName}>{props.student.graduationYear}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        label={graduationYearDisplayField?.displayName}
        required={graduationYearDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.student.graduationYear}
        onChange={(e) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.graduationYear = parseOptionalNumber(e.target.value);
            })
          )
        }
      />
    );
  };

  const graduationTypeField = () => {
    const propertyName = pn<Student>("graduationType");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={graduationTypeDisplayField?.displayName}>{props.student.graduationType}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        label={graduationTypeDisplayField?.displayName}
        required={graduationTypeDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.student.graduationType}
        onChange={(e) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.graduationType = e.target.value;
            })
          )
        }
      />
    );
  };

  const familyIncomeField = () => {
    const propertyName = pn<Student>("familyIncomeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={familyIncomeDisplayField?.displayName}>{props.student.familyIncome?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={familyIncomeDisplayField?.displayName}
        required={familyIncomeDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.familyIncomes?.find((x) => x.id === props.student.familyIncomeId)}
        options={props.editResource.familyIncomes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.familyIncomeId = value?.id;
            })
          )
        }
      />
    );
  };

  const cohortField = () => {
    const propertyName = pn<Student>("cohortId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={cohortDisplayField?.displayName}>{props.student.cohort?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={cohortDisplayField?.displayName}
        required={cohortDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.cohorts?.find((x) => x.id === props.student.cohortId)}
        options={props.editResource.cohorts}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.cohortId = value?.id;
            })
          )
        }
      />
    );
  };

  const noCollegeReasonField = () => {
    const propertyName = pn<Student>("noCollegeReasonId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={noCollegeReasonDisplayField?.displayName}>{props.student.noCollegeReason?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={noCollegeReasonDisplayField?.displayName}
        required={noCollegeReasonDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.noCollegeReasons?.find((x) => x.id === props.student.noCollegeReasonId)}
        options={props.editResource.noCollegeReasons}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.noCollegeReasonId = value?.id;
            })
          )
        }
      />
    );
  };

  const ninthGradeYearField = () => {
    const propertyName = pn<Student>("ninthGradeYear");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={ninthGradeYearDisplayField?.displayName}>{props.student.ninthGradeYear}</DataDisplay>
    ) : (
      <Input
        label={ninthGradeYearDisplayField?.displayName}
        required={ninthGradeYearDisplayField?.required}
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.student.ninthGradeYear}
        onChange={(e) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.ninthGradeYear = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  const referralTypeField = () => {
    const propertyName = pn<Student>("referralTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={referralTypeDisplayField?.displayName}>{props.student.referralType?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={referralTypeDisplayField?.displayName}
        required={referralTypeDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.referralTypes?.find((x) => x.id === props.student.referralTypeId)}
        options={props.editResource.referralTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.referralTypeId = value?.id;
            })
          )
        }
      />
    );
  };

  const reasonForEnrollmentField = () => {
    const propertyName = pn<Student>("reasonForEnrollmentId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={reasonForEnrollmentDisplayField?.displayName}>{props.student.reasonForEnrollment?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={reasonForEnrollmentDisplayField?.displayName}
        required={reasonForEnrollmentDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.reasonsForEnrollment?.find((x) => x.id === props.student.reasonForEnrollmentId)}
        options={props.editResource.reasonsForEnrollment}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.reasonForEnrollmentId = value?.id;
            })
          )
        }
      />
    );
  };

  const primaryTransportationField = () => {
    const propertyName = pn<Student>("primaryTransportationId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={primaryTransportationDisplayField?.displayName}>{props.student.primaryTransportation?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={primaryTransportationDisplayField?.displayName}
        required={primaryTransportationDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.primaryTransportations?.find((x) => x.id === props.student.primaryTransportationId)}
        options={props.editResource.primaryTransportations}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.primaryTransportationId = value?.id;
            })
          )
        }
      />
    );
  };

  const eligibilityField = () => {
    const propertyName = pn<Student>("eligibilityId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={eligibilityDisplayField?.displayName}>{props.student.eligibility?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={eligibilityDisplayField?.displayName}
        required={eligibilityDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.eligibilities?.find((x) => x.id === props.student.eligibilityId)}
        options={props.editResource.eligibilities}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.eligibilityId = value?.id;
            })
          )
        }
      />
    );
  };

  const eligibilityNoteField = () => {
    const propertyName = pn<Student>("eligibilityNote");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.student.eligibilityNote}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.student.eligibilityNote}
        onChange={(e) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.eligibilityNote = e.target.value;
            })
          )
        }
      />
    );
  };

  const academicNeedField = () => {
    const propertyName = pn<Student>("academicNeedId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={academicNeedDisplayField?.displayName}>{props.student.academicNeed?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={academicNeedDisplayField?.displayName}
        required={academicNeedDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.academicNeeds?.find((x) => x.id === props.student.academicNeedId)}
        options={props.editResource.academicNeeds}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.academicNeedId = value?.id;
            })
          )
        }
      />
    );
  };

  const academicNeedNoteField = () => {
    const propertyName = pn<Student>("academicNeedNote");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.student.academicNeedNote}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.student.academicNeedNote}
        onChange={(e) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.academicNeedNote = e.target.value;
            })
          )
        }
      />
    );
  };



  const permissionSlipField = () => {
    const propertyName = pn<Student>("permissionSlip");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={permissionSlipDisplayField?.displayName}>{formatBoolean(props.student.permissionSlip)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={permissionSlipDisplayField?.displayName}
        required={permissionSlipDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS?.find((x) => x.id === props.student.permissionSlip)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.permissionSlip = value?.id;
            })
          )
        }
      />
    );
  };

  const collegeBoundField = () => {
    const propertyName = pn<Student>("collegeBound");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={collegeBoundDisplayField?.displayName}>{formatBoolean(props.student.collegeBound)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={collegeBoundDisplayField?.displayName}
        required={collegeBoundDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS?.find((x) => x.id === props.student.collegeBound)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.collegeBound = value?.id;
            })
          )
        }
      />
    );
  };

  const mentorProgramField = () => {
    const propertyName = pn<Student>("mentorProgram");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={mentorProgramDisplayField?.displayName}>{formatBoolean(props.student.mentorProgram)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={mentorProgramDisplayField?.displayName}
        required={mentorProgramDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS?.find((x) => x.id === props.student.mentorProgram)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.mentorProgram = value?.id;
            })
          )
        }
      />
    );
  };

  const migrantField = () => {
    const propertyName = pn<Student>("migrant");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={migrantDisplayField?.displayName}>{formatBoolean(props.student.migrant)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={migrantDisplayField?.displayName}
        required={migrantDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS?.find((x) => x.id === props.student.migrant)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.migrant = value?.id;
            })
          )
        }
      />
    );
  };

  const sixYearsEslField = () => {
    const propertyName = pn<Student>("sixYearsEsl");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={sixYearsEslDisplayField?.displayName}>{formatBoolean(props.student.sixYearsEsl)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={sixYearsEslDisplayField?.displayName}
        required={sixYearsEslDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS?.find((x) => x.id === props.student.sixYearsEsl)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.sixYearsEsl = value?.id;
            })
          )
        }
      />
    );
  };

  const dualEnrollmentField = () => {
    const propertyName = pn<Student>("dualEnrollment");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={dualEnrollmentDisplayField?.displayName}>{formatBoolean(props.student.dualEnrollment)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={dualEnrollmentDisplayField?.displayName}
        required={dualEnrollmentDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS?.find((x) => x.id === props.student.dualEnrollment)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.dualEnrollment = value?.id;
            })
          )
        }
      />
    );
  };

  const povertyField = () => {
    const propertyName = pn<Student>("poverty");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={povertyDisplayField?.displayName}>{formatBoolean(props.student.poverty)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={povertyDisplayField?.displayName}
        required={povertyDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS?.find((x) => x.id === props.student.poverty)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.poverty = value?.id;
            })
          )
        }
      />
    );
  };

  const firstGenerationField = () => {
    const propertyName = pn<Student>("firstGeneration");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={firstGenerationDisplayField?.displayName}>{formatBoolean(props.student.firstGeneration)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={firstGenerationDisplayField?.displayName}
        required={firstGenerationDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS?.find((x) => x.id === props.student.firstGeneration)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.firstGeneration = value?.id;
            })
          )
        }
      />
    );
  };

  const trioField = () => {
    const propertyName = pn<Student>("trio");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={trioDisplayField?.displayName}>{formatBoolean(props.student.trio)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={trioDisplayField?.displayName}
        required={trioDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS?.find((x) => x.id === props.student.trio)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.trio = value?.id;
            })
          )
        }
      />
    );
  };

  const upwardBoundField = () => {
    const propertyName = pn<Student>("upwardBound");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={upwardBoundDisplayField?.displayName}>{formatBoolean(props.student.upwardBound)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={upwardBoundDisplayField?.displayName}
        required={upwardBoundDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS?.find((x) => x.id === props.student.upwardBound)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.upwardBound = value?.id;
            })
          )
        }
      />
    );
  };

  const eopsField = () => {
    const propertyName = pn<Student>("eops");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={eopsDisplayField?.displayName}>{formatBoolean(props.student.eops)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={eopsDisplayField?.displayName}
        required={eopsDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS?.find((x) => x.id === props.student.eops)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.eops = value?.id;
            })
          )
        }
      />
    );
  };

  const usCitizenField = () => {
    const propertyName = pn<Student>("usCitizen");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={usCitizenDisplayField?.displayName}>{formatBoolean(props.student.usCitizen)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={usCitizenDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS?.find((x) => x.id === props.student.usCitizen)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.usCitizen = value?.id;
            })
          )
        }
      />
    );
  };

  const secondaryEmailField = () => {
    const propertyName = pn<Student>("secondaryEmail");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={secondaryEmailDisplayField?.displayName}>{props.student.secondaryEmail}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        label={secondaryEmailDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.student.secondaryEmail}
        onChange={(e) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.secondaryEmail = e.target.value;
            })
          )
        }
      />
    );
  };

  const pseCohortField = () => {
    const propertyName = pn<Student>("pseCohort");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={pseCohortDisplayField?.displayName}>{props.student.pseCohort}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        label={pseCohortDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.student.pseCohort}
        type="number"
        onChange={(e) =>
          props.setStudent(
            produce(props.student, (draft) => {
              const value = e.target.value;

              draft.pseCohort =
                value === "" ? undefined : parseInt(value, 10);
            })
          )
        }
      />
    );
  };

  const pseFirstEnrollmentDateField = () => {
    const propertyName = pn<Student>("pseFirstEnrollment");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={pseFirstEnrollmentDateDisplayField?.displayName}>{props.student.pseFirstEnrollment}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        label={pseFirstEnrollmentDateDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.student.pseFirstEnrollment}
        onChange={(value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.pseFirstEnrollment = value;
            })
          )
        }
      />
    );
  };

  const atRiskStdTestField = () => {
    const propertyName = pn<Student>("atRiskStdTestId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={atRiskStdTestDisplayField?.displayName}>{props.student.atRiskStdTest?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required={atRiskStdTestDisplayField?.required}
        label={atRiskStdTestDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.atRiskStdTests?.find((x) => x.id === props.student.atRiskStdTestId)}
        options={props.editResource.atRiskStdTests}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.atRiskStdTestId = value?.id;
            })
          )
        }
      />
    );
  };

  const atRiskAlgebraField = () => {
    const propertyName = pn<Student>("atRiskAlgebraId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={atRiskAlgebraDisplayField?.displayName}>{props.student.atRiskAlgebra?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required={atRiskAlgebraDisplayField?.required}
        label={atRiskAlgebraDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.atRiskAlgebras?.find((x) => x.id === props.student.atRiskAlgebraId)}
        options={props.editResource.atRiskAlgebras}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.atRiskAlgebraId = value?.id;
            })
          )
        }
      />
    );
  };

  const atRiskLowGpaField = () => {
    const propertyName = pn<Student>("atRiskLowGpaId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={atRiskLowGpaDisplayField?.displayName}>{props.student.atRiskLowGpa?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required={atRiskLowGpaDisplayField?.required}
        label={atRiskLowGpaDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.atRiskLowGpas?.find((x) => x.id === props.student.atRiskLowGpaId)}
        options={props.editResource.atRiskLowGpas}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.atRiskLowGpaId = value?.id;
            })
          )
        }
      />
    );
  };

  const otherNeedField = () => {
    const propertyName = pn<Student>("otherNeedId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={otherNeedDisplayField?.displayName}>{props.student.otherNeed?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={otherNeedDisplayField?.displayName}
        required={otherNeedDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.otherNeeds?.find((x) => x.id === props.student.otherNeedId)}
        options={props.editResource.otherNeeds}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.otherNeedId = value?.id;
            })
          )
        }
      />
    );
  };

  const deceasedField = () => {
    const propertyName = pn<Student>("deceasedId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={deceasedDisplayField?.displayName}>{props.student.deceased?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={deceasedDisplayField?.displayName}
        required={deceasedDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.deceased?.find((x) => x.id === props.student.deceasedId)}
        options={props.editResource.deceased}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.deceasedId = value?.id;
            })
          )
        }
      />
    );
  };

  const disabledField = () => {
    const propertyName = pn<Student>("disabledId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={disabledDisplayField?.displayName}>{props.student.disabled?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={disabledDisplayField?.displayName}
        required={disabledDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.disabled?.find((x) => x.id === props.student.disabledId)}
        options={props.editResource.disabled}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.disabledId = value?.id;
            })
          )
        }
      />
    );
  };

  const otherSecondaryNeedField = () => {
    const propertyName = pn<Student>("otherSecondaryNeedId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={otherSecondaryNeedDisplayField?.displayName}>{props.student.otherSecondaryNeed?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={otherSecondaryNeedDisplayField?.displayName}
        required={otherSecondaryNeedDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.otherNeeds?.find((x) => x.id === props.student.otherSecondaryNeedId)}
        options={props.editResource.otherNeeds}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.otherSecondaryNeedId = value?.id;
            })
          )
        }
      />
    );
  };

  const disconnectedField = () => {
    const propertyName = pn<Student>("disconnected");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={disconnectedDisplayField?.displayName}>
        {formatBoolean(props.student.disconnected)}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required={disconnectedDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        label={disconnectedDisplayField?.displayName}
        value={BOOLEAN_SELECT_LIST_ITEMS?.find((x) => x.id === props.student.disconnected)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.disconnected = value?.id;
            })
          )
        }
      />
    );
  };

  const UnresponsiveField = () => {
    const propertyName = pn<Student>("unresponsive");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={unresponsiveDisplayField?.displayName}>
        {formatBoolean(props.student.unresponsive)}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={unresponsiveDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.student.unresponsive)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setStudent(
            produce(props.student, (draft) => {
              draft.unresponsive = value?.id;
            })
          )
        }
      />
    );
  };

  const FIELD_COMPONENT_MAP: Partial<Record<LkStudentDisplayFieldId, React.FC<any>>> = {
    [LkStudentDisplayFieldId.Unresponsive]: UnresponsiveField,
    [LkStudentDisplayFieldId.FeederSchool]: feederSchoolField,
    [LkStudentDisplayFieldId.EnrollmentDate]: enrollmentDateField,
    [LkStudentDisplayFieldId.UsCitizen]: usCitizenField,
    [LkStudentDisplayFieldId.AtRiskStdTest]: atRiskStdTestField,
    [LkStudentDisplayFieldId.AtRiskAlgebra]: atRiskAlgebraField,
    [LkStudentDisplayFieldId.AtRiskLowGpa]: atRiskLowGpaField,
    [LkStudentDisplayFieldId.OtherNeed]: otherNeedField,
    [LkStudentDisplayFieldId.Disconnected]: disconnectedField,
    [LkStudentDisplayFieldId.OtherSecondaryNeed]: otherSecondaryNeedField,
    [LkStudentDisplayFieldId.Deceased]: deceasedField,
    [LkStudentDisplayFieldId.Disabled]: disabledField,
    [LkStudentDisplayFieldId.PseCohort]: pseCohortField,
    [LkStudentDisplayFieldId.PseFirstEnrollmentDate]: pseFirstEnrollmentDateField,
    [LkStudentDisplayFieldId.NinthGradeYear]: ninthGradeYearField,
    [LkStudentDisplayFieldId.PermissionSlip]: permissionSlipField,
    [LkStudentDisplayFieldId.CollegeBound]: collegeBoundField,
    [LkStudentDisplayFieldId.MentorProgram]: mentorProgramField,
    [LkStudentDisplayFieldId.Migrant]: migrantField,
    [LkStudentDisplayFieldId.SixYearsEsl]: sixYearsEslField,
    [LkStudentDisplayFieldId.DualEnrollment]: dualEnrollmentField,
    [LkStudentDisplayFieldId.Poverty]: povertyField,
    [LkStudentDisplayFieldId.FirstGeneration]: firstGenerationField,
    [LkStudentDisplayFieldId.Trio]: trioField,
    [LkStudentDisplayFieldId.UpwardBound]: upwardBoundField,
    [LkStudentDisplayFieldId.Eops]: eopsField,
    [LkStudentDisplayFieldId.ReferralType]: referralTypeField,
    [LkStudentDisplayFieldId.PrimaryTransportation]: primaryTransportationField,
    [LkStudentDisplayFieldId.ReasonForEnrollment]: reasonForEnrollmentField,
    [LkStudentDisplayFieldId.GraduationYear]: GraduationYearField,
    [LkStudentDisplayFieldId.GraduationType]: graduationTypeField,
    [LkStudentDisplayFieldId.FamilyIncome]: familyIncomeField,
    [LkStudentDisplayFieldId.Cohort]: cohortField,
    [LkStudentDisplayFieldId.NoCollegeReason]: noCollegeReasonField,
    [LkStudentDisplayFieldId.Eligibility]: eligibilityField,
    [LkStudentDisplayFieldId.AcademicNeed]: academicNeedField,

  };
  const renderDisplayFields = () => {
    return sortedFields.map((field) => {

      const renderFieldFunc = FIELD_COMPONENT_MAP[field.id];

      if (!renderFieldFunc) {
        console.warn(`No React component mapped for field name: ${field.name}`);
        return null;
      }

      return (
        <Grid key={field.id} item xs={12} md={3}>
          {renderFieldFunc()}
        </Grid>
      );
    })
  };

  return (
    <Stack >
      <Stack direction={{ xs: "column", sm: "column", md: "row", lg: "row" }}
        sx={{ height: "100%", flex: 1 }}>
        <Grid container>
          <Grid item lg={3} paddingRight="10px" sx={{
            pr: { md: "10px", xs: 0 },
            pb: { xs: "10px", sm: "10px", md: 0 }, display: "flex", flexDirection: "column"
          }}>
            <Box
              sx={{
                borderRadius: 3,
                border: "1px solid #ddd",
                overflow: "hidden",
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                background: "#fff",
              }}
            >

              <Box
                sx={{
                  background: "#0d57a1",
                  color: "white",
                  px: 2,
                  py: 1,
                  fontSize: "20px",
                  fontWeight: 600,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box component="span" sx={{ paddingLeft: 2 }}>
                  Info
                </Box>

                <Stack direction="row" alignItems="center">
                  <DataModelInformation dataModelType={DataModelEnum.Student} dataModelId={id} />

                  {(props.crudMode === "edit" &&
                    props.save != undefined &&
                    isAuthorized(Permission.Student, PermissionAction.Lock)) && (
                      <LockButton
                        tooltip="Allow editing of locked properties"
                        lock={isLocked}
                        onChange={() => setIsLocked(!isLocked)}
                      />
                    )}

                  {props.hasEditButton &&
                    props.crudMode === "view" &&
                    isAuthorized(Permission.Student, PermissionAction.Update) && (
                      <UpdateIconButton color="info" onClick={() => props.reroute?.(id, "edit")} />
                    )}

                  {(props.crudMode === "edit" && props.save != undefined) && (
                    <SubmitIconButton onClick={() => props.save!()} disabled={!props.unsavedChanges} />
                  )}

                  {props.hasEditButton &&
                    props.crudMode === "edit" &&
                    isAuthorized(Permission.Student, PermissionAction.Update) && (
                      <CancelIconButton onClick={() => props.reroute?.(id, "view")} />
                    )}
                </Stack>
              </Box>

              <Box sx={{ p: 3 }}>
                <Grid container padding={2} spacing="sm">
                  <Grid item xs={12}>
                    {programDisplayField && (
                      <Grid>
                        {programField()}
                      </Grid>
                    )}
                  </Grid>
                  {props.viewResource.customIdTypes?.map((x) => (
                    <Grid item xs={12} key={x.id}>
                      {customField(x.id, x)}
                    </Grid>
                  ))}
                  <Grid item xs={12}>{firstNameField()}</Grid>
                  <Grid item xs={12}>{middleNameField()}</Grid>
                  <Grid item xs={12}>{lastNameField()}</Grid>
                  <Grid item xs={12}>{suffixField()}</Grid>

                  {maidenNameDisplayField && (
                    <Grid item xs={12}>{maidenNameField()}</Grid>
                  )}

                  <Grid item xs={12}>{nickNameField()}</Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} lg={9}>
            <Stack direction="column" sx={{ flexGrow: 1, gap: 2, height: "100%", overflow: "auto" }}>
              <Box sx={{ borderRadius: 3, border: "1px solid #ddd", overflow: "hidden", background: "#fff", }}>
                <Grid
                  container
                  sx={{
                    background: "#0d57a1",
                    color: "white",
                    px: 2,
                    py: 1.5,
                    fontSize: "20px",
                    fontWeight: 600,
                    alignItems: "center",
                  }}
                >
                  <Grid item xs={12} md={4} textAlign="left" paddingLeft={2}>
                    Contact
                  </Grid>

                  <Grid item xs={12} md={4} textAlign="left" paddingLeft={2}>
                    Address
                  </Grid>

                  <Grid item xs={12} md={3} paddingLeft={2} sx={{ display: "flex", justifyContent: "left", position: "relative" }}>
                    Demographics
                  </Grid>
                  <Grid md={1} >
                    <Stack direction="row" alignItems="center" justifyContent="end">

                      {(props.crudMode === "edit" &&
                        props.save != undefined &&
                        isAuthorized(Permission.Student, PermissionAction.Lock)) && (
                          <LockButton
                            tooltip="Allow editing of locked properties"
                            lock={isLocked}
                            onChange={() => setIsLocked(!isLocked)}
                          />
                        )}

                      {props.hasEditButton &&
                        props.crudMode === "view" &&
                        isAuthorized(Permission.Student, PermissionAction.Update) && (
                          <UpdateIconButton color="info" onClick={() => props.reroute?.(id, "edit")} />
                        )}

                      {(props.crudMode === "edit" && props.save != undefined) && (
                        <SubmitIconButton onClick={() => props.save!()} disabled={!props.unsavedChanges} />
                      )}

                      {props.hasEditButton &&
                        props.crudMode === "edit" &&
                        isAuthorized(Permission.Student, PermissionAction.Update) && (
                          <CancelIconButton onClick={() => props.reroute?.(id, "view")} />
                        )}
                    </Stack>
                  </Grid>
                </Grid>

                <Box sx={{ p: 3 }}>
                  <Grid container>
                    {/* CONTACT */}
                    <Grid item xs={4}>
                      <Grid container padding={2} spacing="sm">
                        <Grid item xs={12}>
                          {emailField()}
                        </Grid>
                        {secondaryEmailDisplayField && (
                          <Grid item xs={12}>
                            {secondaryEmailField()}
                          </Grid>)}
                        <Grid item xs={12}>
                          <Stack direction="row" width="100%">
                            {cellPhoneField()}
                            {/* {props.tenantTextingInfo?.canInfobip && (
                              <Stack direction="row" alignItems="center" spacing="sm">
                                {(!cellPhoneVerified) && props.student.cellPhone && (<Button onMouseDown={() => verifyPhoneNumber()}>Verify</Button>)}
                                {(cellPhoneVerified != null && cellPhoneVerified) && (<Tooltip title="Cell Phone Verified">
                                  <CheckCircleOutlineIcon style={{ color: "green", cursor: "pointer" }} /></Tooltip>)}
                                {(cellPhoneVerified != null && !cellPhoneVerified) && (<Typography color="error" variant="body1">Unable to Verify</Typography>)}
                              </Stack>
                            )} */}
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          {textingOptInFields()}
                        </Grid>
                        {phoneNumberDisplayField && (
                          <Grid item xs={12}>
                            {phoneField()}
                          </Grid>
                        )}
                        {workPhoneNumberDisplayField && (
                          <Grid item xs={12}>
                            {workPhoneField()}
                          </Grid>
                        )}
                      </Grid>
                    </Grid>

                    {/* ADDRESS */}
                    <Grid item xs={4}>
                      <Grid container padding={2}>
                        <Grid item xs={12}>
                          <AddressFields
                            crudMode={props.crudMode}
                            properties={properties.find((x) => x.propertyName === pn<Student>("address"))?.children || []}
                            address={props.student.address || ({} as Address)}
                            setAddress={(address) =>
                              props.setStudent(
                                produce(props.student, (draft) => {
                                  draft.address = address;
                                })
                              )
                            }
                            validationErrors={props.validationErrors[pn<Student>("address")] || {}}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* DEMOGRAPHICS */}
                    <Grid item xs={4}>
                      <Grid container padding={2} spacing="sm">
                        <Grid item xs={12}>{dateOfBirthField()}</Grid>
                        <Grid item xs={12} >{ageField()}</Grid>
                        <Grid item xs={12} >{ethnicityField()}</Grid>
                        <Grid item xs={12} >{raceField()}</Grid>
                        {props.student.raceId === LkRaceId.TwoOrMoreRaces && (
                          <Grid item xs={12} >
                            {racesField()}
                          </Grid>
                        )}
                        <Grid item xs={12} >{genderField()}</Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Box>


              <Box
                sx={{
                  borderRadius: 3,
                  border: "1px solid #ddd",
                  overflow: "hidden",
                  background: "#fff",
                }}
              >
                <Box
                  sx={{
                    background: "#0d57a1",
                    color: "white",
                    px: 2,
                    py: 1.5,
                    fontSize: "20px",
                    fontWeight: 600,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box component="span" sx={{ paddingLeft: 2 }}>Additional Details</Box>
                  <Stack direction="row" alignItems="center">

                    {(props.crudMode === "edit" &&
                      props.save != undefined &&
                      isAuthorized(Permission.Student, PermissionAction.Lock)) && (
                        <LockButton
                          tooltip="Allow editing of locked properties"
                          lock={isLocked}
                          onChange={() => setIsLocked(!isLocked)}
                        />
                      )}

                    {props.hasEditButton &&
                      props.crudMode === "view" &&
                      isAuthorized(Permission.Student, PermissionAction.Update) && (
                        <UpdateIconButton color="info" onClick={() => props.reroute?.(id, "edit")} />
                      )}

                    {(props.crudMode === "edit" && props.save != undefined) && (
                      <SubmitIconButton onClick={() => props.save!()} disabled={!props.unsavedChanges} />
                    )}

                    {props.hasEditButton &&
                      props.crudMode === "edit" &&
                      isAuthorized(Permission.Student, PermissionAction.Update) && (
                        <CancelIconButton onClick={() => props.reroute?.(id, "view")} />
                      )}
                  </Stack>
                </Box>

                <Box sx={{ p: 3, }}>
                  <Grid container padding={2} spacing="sm">
                    {renderDisplayFields()}

                    {props.viewResource.customDataModelFields?.map((field) => {
                      return (
                        <Grid key={field.id} item xs={12} md={3}>
                          {props.crudMode === "view" && (
                            <DataDisplay label={field.name}>
                              <CustomDataModelFieldDisplay field={field} dataModel={props.student} />
                            </DataDisplay>
                          )}

                          {props.crudMode === "edit" && (
                            <CustomDataModelFieldEditor
                              field={field}
                              dataModel={props.student}
                              setDataModel={(student) => props.setStudent(student)}
                              errors={props.validationErrors[field.fieldNameCamelCase!]}
                            />
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>

                  {/* Used to be Variables moving to display fields */}
                  <Grid container spacing="sm" padding={2}>
                    {eligibilityDisplayField && props.student.eligibilityId === LkEligibilityId.Other && (
                      <Grid item xs={12} md={3}>
                        {eligibilityNoteField()}
                      </Grid>
                    )}

                    {academicNeedDisplayField &&
                      props.student.academicNeedId === LkAcademicNeedId.Other && (
                        <Grid item xs={12} md={3}>
                          {academicNeedNoteField()}
                        </Grid>
                      )}
                  </Grid>
                </Box>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Stack >
  );

};
